import type { BoundaryResult } from "./types";

const START_BOUNDARY = /^##\s+SITUATION\s*:?\s*$/i;
const END_BOUNDARY =
  /^##\s+(?:Training Delivery Notes|Conversion Audit Log)\b/i;
const DOCUMENT_TITLE = /^#\s+(.+?)\s*$/;
const WARNING_ORDER_TITLE = /\b(?:WARNING\s+ORDER|WNG\s*O|WNGO)\b/i;
const LEVEL_TWO_HEADING = /^##\s+\S/;

function warningOrderHeading(line: string): boolean {
  const match = /^#{1,2}\s+(.+?)\s*$/.exec(line);
  return match?.[1] !== undefined && WARNING_ORDER_TITLE.test(match[1]);
}

interface BoundaryProfile {
  readonly missingStartMessage: string;
  readonly startIndex: (lines: readonly string[]) => number;
}

const ORDERS_PROFILE: BoundaryProfile = {
  missingStartMessage: "The orders file has no Situation start boundary.",
  startIndex: (lines) => lines.findIndex((line) => START_BOUNDARY.test(line)),
};

const WARNING_ORDER_PROFILE: BoundaryProfile = {
  missingStartMessage:
    "The warning-order file has no recognized title and content boundary.",
  startIndex: (lines) => {
    const firstLevelTwo = lines.findIndex(
      (line) => LEVEL_TWO_HEADING.test(line) && !END_BOUNDARY.test(line),
    );
    if (firstLevelTwo < 0) return -1;
    const title = documentTitle(lines, firstLevelTwo);
    if (title !== null && WARNING_ORDER_TITLE.test(title)) return firstLevelTwo;

    return lines.findIndex(
      (line) => /^##\s+/.test(line) && warningOrderHeading(line),
    );
  },
};

function documentTitle(
  lines: readonly string[],
  endIndex: number,
): string | null {
  for (const line of lines.slice(0, endIndex)) {
    const match = DOCUMENT_TITLE.exec(line);
    if (match !== null) return line.replace(/^#\s+/, "").trim();
  }

  return null;
}

/**
 * Whether a heading names the document kind the profile already selected.
 *
 * Ruling 55 of 8 October 2026: such a heading is the document's title. It is
 * not an unrecognised heading, it does not go in the synonym table, and it
 * is not mapped to a SMESC paragraph. The warning-order profile is chosen
 * *because* the file carries this heading, so warning about it afterwards
 * told the trainee his document barely parsed when it had parsed correctly.
 */
export function headingNamesDocument(
  kind: "orders" | "warning-order",
  heading: string,
): boolean {
  return kind === "warning-order" && WARNING_ORDER_TITLE.test(heading);
}

export function isWarningOrderSource(source: string): boolean {
  return source
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .split("\n")
    .some(warningOrderHeading);
}

function extractWithProfile(
  source: string,
  profile: BoundaryProfile,
): BoundaryResult {
  const lines = source
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .split("\n");
  const startIndex = profile.startIndex(lines);

  if (startIndex < 0) {
    return {
      ok: false,
      code: "MISSING_START_BOUNDARY",
      message: profile.missingStartMessage,
    };
  }

  // Doc 04 section 5.1, amended 12 September 2026: the end boundary is
  // optional. Where a Training Delivery Notes or Conversion Audit Log heading
  // is present the body ends there and neither section is ever delivered.
  // Where neither is present the body ends at end of file, which is not an
  // error. The rule keeps the audit log out of delivery; it never required an
  // order set to carry a conversion artefact, and a hand-authored set has no
  // conversion history to declare.
  const relativeEndIndex = lines
    .slice(startIndex + 1)
    .findIndex((line) => END_BOUNDARY.test(line));
  const endIndex =
    relativeEndIndex < 0 ? lines.length : startIndex + 1 + relativeEndIndex;
  return {
    ok: true,
    title: documentTitle(lines, startIndex),
    body: lines.slice(startIndex, endIndex).join("\n"),
    startLine: startIndex + 1,
    endLine: endIndex + 1,
  };
}

export function extractDeliverySource(source: string): BoundaryResult {
  return extractWithProfile(source, ORDERS_PROFILE);
}

export function extractWarningOrderDeliverySource(
  source: string,
): BoundaryResult {
  return extractWithProfile(source, WARNING_ORDER_PROFILE);
}
